import { ListFooter, ListPagination, ToolbarSearch } from '../ui/ListToolbar';
import { PERSON_NONE, PersonSelect } from '../ui/PersonSelect';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Archive, CheckCircle, ChevronDown, Clock, SlidersHorizontal, Star, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';
import { Spinner } from '../ui/Spinner';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketCategoryBadge } from './TicketCategoryBadge';
import { TicketPoleBadge } from './TicketPoleBadge';
import { getDisplayStatus } from './ticketStatusDisplay';
import { useTickets } from '../../hooks/queries/useTickets';
import { useTicketCategories } from '../../hooks/queries/useTicketCategories';
import { useTicketStore } from '../../store/useTicketStore';
import { useAuthStore } from '../../store/useAuthStore';
import { cn } from '../../lib/cn';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import {
  classifyTicketTab,
  TICKET_TABS,
  TICKET_TAB_LABELS,
  type TicketTab,
} from '../../services/tickets/ticketService';
import { DEMANDES_PAGE_SIZE, paginateList } from '../../services/tickets/ticketListPagination';
import {
  clientFollowUpDisplay,
  isTicketValidated,
} from '../../services/tickets/clientFollowUp';
import { useTabVisibilityStore } from '../../store/useTabVisibilityStore';
import { usePublishPageTabSettings } from '../../store/usePageSettingsStore';

import { ResizableColumns } from '../ui/ResizableColumns';
/** Extrait le sujet et la demande encodés par le webhook dans le champ message */
export function parseTicketMessage(message: string): { subject: string | null; body: string } {
  const subjectMatch = message.match(/^Sujet\s*:\s*(.+?)(?:\n|$)/m);
  const subject = subjectMatch?.[1]?.trim() ?? null;
  // Le corps du message est après la double ligne vide qui suit les métadonnées
  const bodyStart = message.indexOf('\n\n');
  const body = bodyStart !== -1 ? message.slice(bodyStart + 2).trim() : message;
  return { subject, body };
}

const STATUSES = [
  { value: 'new', label: 'Nouveau' },
  { value: 'in_progress', label: 'Pris en charge' },
] as const;

// ─── Multi-select dropdown ───────────────────────────────────────────────────

interface MultiSelectDropdownProps {
  label: string;
  options: readonly { value: string; label: string; group?: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  // Build grouped structure: [{ group: string | null, items: option[] }]
  const grouped = options.reduce<{ group: string | null; items: typeof options[number][] }[]>(
    (acc, opt) => {
      const groupName = opt.group ?? null;
      const existing = acc.find((g) => g.group === groupName);
      if (existing) {
        existing.items.push(opt);
      } else {
        acc.push({ group: groupName, items: [opt] });
      }
      return acc;
    },
    [],
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-[11px] text-xs transition-colors',
          selected.length > 0
            ? 'border-cime-primary bg-[rgba(4,52,41,0.07)] font-semibold text-cime-primary'
            : 'border-dashed border-neutral-300 bg-transparent text-cime-text-muted hover:border-cime-primary hover:text-cime-primary',
        )}
      >
        {label}
        {selected.length > 0 && (
          <span className="grid h-[17px] min-w-[17px] place-items-center rounded-full bg-cime-primary px-[5px] text-[10px] font-bold text-white">
            {selected.length}
          </span>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-neutral-200 rounded-cime shadow-lg py-1 min-w-48 max-h-80 overflow-y-auto">
            {grouped.map(({ group, items }) => (
              <div key={group ?? '__ungrouped'}>
                {group && (
                  <p className="px-3 pt-2 pb-0.5 text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">
                    {group}
                  </p>
                )}
                {items.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      className="rounded border-neutral-300 accent-cime-primary"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function TicketsListPage() {
  const navigate = useNavigate();
  const { filters, setFilter, resetFilters, activeTab, setActiveTab } = useTicketStore();
  const collaborator = useAuthStore((s) => s.collaborator);

  // Debounce search
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter({ search: searchInput || undefined });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setFilter]);

  const [selectedPoles, setSelectedPoles] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const { data: categories } = useTicketCategories();

  // Catégories appartenant à l'onglet courant (mapping = classifyTicketTab).
  // Le pôle et la catégorie ne se filtrent que sur l'onglet Clientèle.
  const tabCategories = useMemo(
    () => (categories ?? []).filter((c) => classifyTicketTab(c) === activeTab),
    [categories, activeTab],
  );

  const poleOptions = Array.from(
    new Set(tabCategories.map((c) => c.pole).filter(Boolean) as string[]),
  ).map((pole) => ({ value: pole, label: pole }));

  const filteredCategories =
    selectedPoles.length > 0
      ? tabCategories.filter((c) => c.pole && selectedPoles.includes(c.pole))
      : tabCategories;

  const { data: allTickets, isLoading } = useTickets(filters);

  // Répartition des tickets dans les 3 onglets (pure, côté client) + compteurs.
  const ticketsByTab = useMemo(() => {
    const buckets: Record<TicketTab, typeof allTickets> = {
      clientele: [],
      recrutement: [],
      autre: [],
    };
    for (const t of allTickets ?? []) {
      (buckets[classifyTicketTab(t.category)] ??= []).push(t);
    }
    return buckets;
  }, [allTickets]);

  const tabCounts = useMemo(
    () => ({
      clientele: ticketsByTab.clientele?.length ?? 0,
      recrutement: ticketsByTab.recrutement?.length ?? 0,
      autre: ticketsByTab.autre?.length ?? 0,
    }),
    [ticketsByTab],
  );

  // Tickets de l'onglet courant, puis filtre fin par pôle/catégorie.
  const tickets = useMemo(() => {
    let list = ticketsByTab[activeTab] ?? [];
    const catIds =
      (filters.category_ids?.length ?? 0) > 0
        ? new Set(filters.category_ids)
        : selectedPoles.length > 0
          ? new Set(
              (categories ?? [])
                .filter((c) => c.pole && selectedPoles.includes(c.pole))
                .map((c) => c.id),
            )
          : null;
    if (catIds) list = list.filter((t) => t.category_id && catIds.has(t.category_id));
    return list;
  }, [ticketsByTab, activeTab, filters.category_ids, selectedPoles, categories]);

  const paged = useMemo(() => paginateList(tickets, page), [tickets, page]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, filters.search, filters.status, filters.category_ids, filters.assigned_to, selectedPoles]);

  useEffect(() => {
    if (paged.page !== page) setPage(paged.page);
  }, [paged.page, page]);

  function handlePolesChange(poles: string[]) {
    setSelectedPoles(poles);
    if (poles.length > 0 && (filters.category_ids?.length ?? 0) > 0) {
      const validIds = new Set(
        (categories ?? [])
          .filter((c) => c.pole && poles.includes(c.pole))
          .map((c) => c.id),
      );
      const kept = (filters.category_ids ?? []).filter((id) => validIds.has(id));
      setFilter({ category_ids: kept.length > 0 ? kept : undefined });
    }
  }

  function handleTabChange(tab: TicketTab) {
    if (tab === activeTab) return;
    setActiveTab(tab);
    // Les sous-filtres pôle/catégorie sont propres à un onglet → on les réinitialise.
    setSelectedPoles([]);
    if ((filters.category_ids?.length ?? 0) > 0) setFilter({ category_ids: undefined });
  }

  // Sélectionner l'objet `hidden` (stable tant qu'il n'est pas muté) — PAS
  // `s.hidden['demandes'] ?? []` qui créerait un nouveau tableau à chaque render
  // (boucle de re-render). On dérive le tableau après le sélecteur.
  const hiddenMap = useTabVisibilityStore((s) => s.hidden);
  const hiddenDemandesTabs = hiddenMap['demandes'] ?? [];

  const visibleTabs = TICKET_TABS.filter((t) => !hiddenDemandesTabs.includes(t));
  // Fall back to all tabs if the user has hidden everything (safety net).
  const renderedTabs = visibleTabs.length > 0 ? visibleTabs : [...TICKET_TABS];

  // Publie la config de réglage d'onglets vers l'AppHeader (icône dédiée).
  usePublishPageTabSettings(
    'demandes',
    TICKET_TABS.map((t) => ({ key: t, label: TICKET_TAB_LABELS[t] })),
  );

  // If the currently active tab got hidden, switch to the first visible one.
  const didSwitchRef = useRef(false);
  useEffect(() => {
    const firstVisible = renderedTabs[0];
    if (firstVisible && hiddenDemandesTabs.includes(activeTab)) {
      if (!didSwitchRef.current) {
        didSwitchRef.current = true;
        handleTabChange(firstVisible);
      }
    } else {
      didSwitchRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, renderedTabs[0], visibleTabs.length]);

  const hasFilters =
    selectedPoles.length > 0 ||
    (filters.status?.length ?? 0) > 0 ||
    (filters.category_ids?.length ?? 0) > 0 ||
    Boolean(filters.assigned_to) ||
    Boolean(filters.search);

  const handleRowClick = useCallback(
    (ticketId: string) => {
      navigate(`/tickets/${ticketId}`);
    },
    [navigate],
  );

  return (
    <div className="main-container-gdm space-y-3">

      {/* Onglets de classification */}
      <div className="flex items-center gap-1 pb-1.5">
        {renderedTabs.map((tab) => {
          const active = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabChange(tab)}
              className={cn(
                'relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2',
                active
                  ? 'border-cime-primary text-cime-primary'
                  : 'border-transparent text-neutral-500 hover:text-neutral-800',
              )}
            >
              {TICKET_TAB_LABELS[tab]}
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold leading-none tabular-nums transition-colors',
                  active
                    ? 'bg-cime-primary text-white'
                    : 'bg-neutral-100 text-neutral-400',
                )}
              >
                {tabCounts[tab]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex min-h-10 flex-wrap items-center gap-2">
        <ToolbarSearch value={searchInput} onChange={setSearchInput} placeholder="Rechercher..." />

        <MultiSelectDropdown
          label="Statut"
          options={STATUSES}
          selected={filters.status ?? []}
          onChange={(v) => setFilter({ status: v.length > 0 ? v : undefined })}
        />

        {activeTab === 'clientele' && (
          <MultiSelectDropdown
            label="Pôle"
            options={poleOptions}
            selected={selectedPoles}
            onChange={handlePolesChange}
          />
        )}

        {activeTab === 'clientele' && (
          <MultiSelectDropdown
            label="Catégorie"
            options={filteredCategories.map((cat) => ({ value: cat.id, label: cat.label, group: cat.pole ?? undefined }))}
            selected={filters.category_ids ?? []}
            onChange={(v) => setFilter({ category_ids: v.length > 0 ? v : undefined })}
          />
        )}

        {/* Assigned to filter */}
        <PersonSelect
          single
          emptyLabel="Tous les intervenants"
          unassignedLabel="Non assignés"
          value={
            filters.assigned_to === 'unassigned'
              ? [PERSON_NONE]
              : filters.assigned_to === 'me'
                ? collaborator?.id ? [collaborator.id] : []
                : filters.assigned_to ? [filters.assigned_to] : []
          }
          onChange={(values) => {
            const val = values[0];
            setFilter({ assigned_to: val === PERSON_NONE ? 'unassigned' : val || undefined });
          }}
        />

        {/* Clear filters */}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              resetFilters();
              setSearchInput('');
              setSelectedPoles([]);
            }}
            className="text-neutral-500"
          >
            <X className="w-4 h-4" />
            Effacer les filtres
          </Button>
        )}

        <span className="min-w-2 flex-1" aria-hidden />
        <button
          type="button"
          onClick={() => navigate('/tickets/archives')}
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3.5 text-[13px] font-medium text-cime-text-primary hover:border-neutral-300"
        >
          <Archive className="w-3.5 h-3.5" />
          Voir les archives
        </button>
      </div>

      {/* Table */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <Spinner />
          </div>
        ) : !tickets || tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <SlidersHorizontal className="w-10 h-10 text-neutral-300 mb-3" />
            {hasFilters ? (
              <>
                <p className="text-neutral-500 font-medium">
                  Aucune demande ne correspond aux filtres.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    resetFilters();
                    setSearchInput('');
                  }}
                >
                  Effacer les filtres
                </Button>
              </>
            ) : (
              <p className="text-neutral-400 max-w-sm">
                Aucune demande pour le moment. Les demandes du site apparaîtront ici
                automatiquement.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="cime-table text-[13px]" role="table">
              <ResizableColumns id="tickets" />
              <thead>
                <tr>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Demande
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Expéditeur
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden sm:table-cell">
                    Assigné à
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    Pôle
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    {activeTab === 'recrutement' ? 'Pré-qualif.' : 'Catégorie'}
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    État client
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    Validé
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Statut
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Reçue le
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.items.map((ticket) => {
                  const isUnseen =
                    ticket.first_seen_at === null &&
                    ticket.assigned_to === collaborator?.id;

                  const timeAgo = formatDistanceToNow(new Date(ticket.created_at), {
                    addSuffix: true,
                    locale: fr,
                  });
                  const fullDate = format(
                    new Date(ticket.created_at),
                    "d MMM yyyy · HH:mm",
                    { locale: fr },
                  );

                  return (
                    <tr
                      key={ticket.id}
                      onClick={() => handleRowClick(ticket.id)}
                      className="cursor-pointer transition-colors"
                    >
                      {/* Excerpt */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] max-w-xs">
                        {(() => {
                          const { subject, body } = parseTicketMessage(ticket.message);
                          return (
                            <>
                              <p className="flex items-center gap-1.5 font-semibold text-neutral-800 text-[13px]">
                                {isUnseen && (
                                  <span
                                    className="w-2 h-2 rounded-full bg-cime-primary flex-shrink-0"
                                    title="Non lu"
                                    aria-label="Non lu"
                                  />
                                )}
                                <span className="truncate">{subject ?? ticket.ai_summary ?? body.slice(0, 60)}</span>
                              </p>
                              <p className="text-cime-text-muted truncate text-xs mt-0.5">
                                {body.slice(0, 80)}{body.length > 80 ? '…' : ''}
                              </p>
                            </>
                          );
                        })()}
                      </td>

                      {/* Sender */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px]">
                        <p className="font-semibold text-[13px] text-neutral-900 truncate max-w-36">
                          {ticket.sender_name ?? 'Inconnu'}
                        </p>
                        <p className="text-xs text-neutral-400 truncate max-w-36">
                          {ticket.sender_email ? (
                            <a
                              href={`mailto:${ticket.sender_email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-cime-primary hover:underline"
                            >
                              {ticket.sender_email}
                            </a>
                          ) : (
                            ''
                          )}
                        </p>
                      </td>

                      {/* Assignee */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden sm:table-cell whitespace-nowrap">
                        {ticket.assignee ? (
                          <CollaboratorLink collaboratorId={ticket.assignee.id} title={`Voir le profil de ${ticket.assignee.full_name}`}>
                            <div className="flex items-center gap-1.5">
                              <Avatar
                                name={ticket.assignee.full_name}
                                src={ticket.assignee.avatar_url ?? undefined}
                                size="xs"
                              />
                              <span className="text-[13px] text-cime-text-primary truncate max-w-24">
                                {ticket.assignee.full_name}
                              </span>
                            </div>
                          </CollaboratorLink>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                      {/* Pole */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        <TicketPoleBadge pole={ticket.category?.pole ?? null} />
                      </td>

                      {/* Category (ou étoiles de pré-qualification sur l'onglet Recrutement) */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        {activeTab === 'recrutement' ? (
                          <RecruitmentStars value={ticket.recruitment_stars} />
                        ) : (
                          <TicketCategoryBadge category={ticket.category} />
                        )}
                      </td>

                      {/* Client status */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        {(() => {
                          const followUp = clientFollowUpDisplay(ticket.client_status);
                          return followUp ? (
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                followUp.className,
                              )}
                            >
                              {followUp.label}
                            </span>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          );
                        })()}
                      </td>

                      {/* Validé = transformé en affaire */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        {isTicketValidated(ticket) ? (
                          <CheckCircle className="w-4 h-4 text-[#2f7a55]" />
                        ) : (
                          <Clock className="w-4 h-4 text-neutral-300" />
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] whitespace-nowrap">
                        <TicketStatusBadge status={getDisplayStatus(ticket)} />
                      </td>

                      {/* Reçue le : toujours la dernière colonne */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] whitespace-nowrap leading-tight">
                        <span className="block text-[13px] font-medium tabular-nums text-cime-text-primary">{fullDate}</span>
                        <span className="block text-xs text-neutral-400">{timeAgo}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {tickets && tickets.length > 0 ? (
          <ListFooter
            pagination={<ListPagination page={paged.page} pageCount={paged.totalPages} onChange={setPage} />}
          >
            {paged.page * DEMANDES_PAGE_SIZE + 1}–{paged.page * DEMANDES_PAGE_SIZE + paged.items.length} sur {paged.total} · tri : reçue le ↓
          </ListFooter>
        ) : null}
      </div>
    </div>
  );
}

// ─── Étoiles de pré-qualification recrutement (lecture seule, vue liste) ───────

function RecruitmentStars({ value }: { value: number | null }) {
  if (!value) return <span className="text-neutral-300">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            'w-3.5 h-3.5',
            i <= value ? 'fill-amber-400 text-amber-400' : 'text-neutral-200',
          )}
        />
      ))}
    </span>
  );
}
